import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { usePlayer } from "@/store/PlayerContext";
import { coverGradientFor } from "@/components/BeatCard";
import PayPalCheckout from "@/components/PayPalCheckout";
import { Play, Pause, Music, Disc, ChevronLeft, Download, FileAudio2, Layers } from "lucide-react";
import { toast } from "sonner";

export default function BeatDetail() {
  const { id } = useParams();
  const { play, current, playing } = usePlayer();
  const [beat, setBeat] = useState(null);
  const [licenseType, setLicenseType] = useState("mp3");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [purchase, setPurchase] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/beats/${id}`);
        setBeat(data);
        if (!data.has_wav) setLicenseType("mp3");
      } catch (e) {
        toast.error("Beat not found");
      }
    })();
  }, [id]);

  if (!beat) {
    return <div className="max-w-5xl mx-auto p-8 text-[var(--text-dim)]">Loading…</div>;
  }
  const isPlaying = current?.id === beat.id && playing;
  const price = licenseType === "bundle" ? beat.price_bundle : beat.price_mp3;
  const gradient = coverGradientFor(beat);

  const downloadLink = (kind) =>
    `${API}/download/${purchase?.orderId}?kind=${kind}&token=${encodeURIComponent(purchase?.download_token || "")}`;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 pb-40 fade-up" data-testid="beat-detail-page">
      <Link to="/" className="inline-flex items-center text-sm text-[var(--text-dim)] hover:text-[var(--text)] mb-6" data-testid="beat-detail-back-link">
        <ChevronLeft className="w-4 h-4 mr-1" /> Back to shop
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="card-base overflow-hidden">
          <div
            className="relative aspect-square border-b-2 border-[var(--ink)]"
            style={gradient ? { background: gradient } : undefined}
          >
            {beat.cover_url ? (
              <img src={beat.cover_url} alt={beat.title} className="w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-display text-9xl font-bold text-[var(--ink)] opacity-70 select-none">
                  {(beat.title || "?").charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <button
              onClick={() => play(beat)}
              className="absolute bottom-5 right-5 btn-primary w-16 h-16 rounded-full flex items-center justify-center pulse-blue"
              data-testid="beat-detail-play-btn"
            >
              {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-0.5" />}
            </button>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap gap-2">
            {beat.genre && <span className="tag-pill" data-testid="beat-detail-genre">{beat.genre}</span>}
            {beat.has_stems && <span className="tag-pill-pink"><Layers className="w-3 h-3 inline mr-0.5" /> Stems</span>}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3" data-testid="beat-detail-title">
            {beat.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[var(--text-dim)] font-mono-cartoon">
            {beat.bpm ? <span className="flex items-center gap-1.5"><Music className="w-3.5 h-3.5" /> {beat.bpm} BPM</span> : null}
            {beat.key_signature ? <span className="flex items-center gap-1.5"><Disc className="w-3.5 h-3.5" /> {beat.key_signature}</span> : null}
          </div>
          {beat.description && <p className="mt-5 text-[var(--text-dim)]">{beat.description}</p>}
          {beat.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {beat.tags.map((t) => (
                <span key={t} className="tag-pill !bg-[var(--bg-elev)] !text-[var(--text)]">#{t}</span>
              ))}
            </div>
          )}

          {/* License options */}
          <div className="mt-8 space-y-3" data-testid="license-options">
            <button
              onClick={() => setLicenseType("mp3")}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                licenseType === "mp3"
                  ? "border-[var(--primary)] bg-[var(--bg-surface)] shadow-[3px_3px_0_0_var(--primary)]"
                  : "border-[var(--ink)] bg-[var(--bg-surface)] hover:border-[var(--primary)]"
              }`}
              data-testid="select-mp3-license"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-[var(--text)] text-lg">MP3 License</div>
                  <div className="text-xs text-[var(--text-dim)] mt-0.5">Streaming-quality MP3 · Non-exclusive</div>
                </div>
                <div className="font-display text-2xl font-bold text-[var(--secondary)]">${beat.price_mp3?.toFixed(2)}</div>
              </div>
            </button>

            <button
              onClick={() => beat.has_wav && setLicenseType("bundle")}
              disabled={!beat.has_wav}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                !beat.has_wav
                  ? "border-[var(--ink)] bg-[var(--bg-surface)] opacity-50 cursor-not-allowed"
                  : licenseType === "bundle"
                  ? "border-[var(--primary)] bg-[var(--bg-surface)] shadow-[3px_3px_0_0_var(--primary)]"
                  : "border-[var(--ink)] bg-[var(--bg-surface)] hover:border-[var(--primary)]"
              }`}
              data-testid="select-bundle-license"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-[var(--text)] text-lg flex items-center gap-2 flex-wrap">
                    {beat.has_stems ? "MP3 + WAV + Stems" : "MP3 + WAV Bundle"}
                    <span className="tag-pill-blue text-[10px]">Studio</span>
                  </div>
                  <div className="text-xs text-[var(--text-dim)] mt-0.5">
                    {beat.has_wav
                      ? (beat.has_stems
                          ? "Pro WAV + trackout stems for mixing + MP3"
                          : "Pro WAV stems for mixing + MP3")
                      : "WAV not available for this beat"}
                  </div>
                </div>
                <div className="font-display text-2xl font-bold text-[var(--secondary)]">${beat.price_bundle?.toFixed(2)}</div>
              </div>
            </button>
          </div>

          {/* Email capture */}
          {!purchase && (
            <div className="mt-6 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--text-dim)] font-bold">Your name (optional)</label>
                <input
                  type="text"
                  className="input-base w-full mt-1 px-3 py-2.5"
                  placeholder="Artist name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--text-dim)] font-bold">Email for download link</label>
                <input
                  type="email"
                  className="input-base w-full mt-1 px-3 py-2.5"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="customer-email-input"
                />
              </div>
              <div className="pt-2">
                <div className="text-xs text-[var(--text-dim)] mb-2">
                  Total: <span className="text-[var(--secondary)] font-bold">${price?.toFixed(2)} USD</span>
                </div>
                <PayPalCheckout
                  beat={beat}
                  licenseType={licenseType}
                  customerEmail={email}
                  customerName={name}
                  disabled={!email}
                  onSuccess={(res) => setPurchase(res)}
                />
              </div>
            </div>
          )}

          {/* Post-purchase download */}
          {purchase && (
            <div className="mt-8 card-flat p-5 border-[var(--secondary)] bg-[var(--bg-surface)]" data-testid="purchase-success-block">
              <div className="font-display font-bold text-[var(--secondary)] text-xl">Purchase complete!</div>
              <p className="text-sm text-[var(--text-dim)] mt-1">Your files are ready. A receipt will be emailed to {email}.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={downloadLink("mp3")}
                  className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
                  data-testid="download-mp3-btn"
                >
                  <Download className="w-4 h-4" /> MP3
                </a>
                {licenseType === "bundle" && (
                  <>
                    <a
                      href={downloadLink("wav")}
                      className="btn-secondary px-4 py-2 text-sm flex items-center gap-2"
                      data-testid="download-wav-btn"
                    >
                      <FileAudio2 className="w-4 h-4" /> WAV
                    </a>
                    {beat.has_stems && (
                      <a
                        href={downloadLink("stems")}
                        className="btn-pink px-4 py-2 text-sm flex items-center gap-2"
                        data-testid="download-stems-btn"
                      >
                        <Layers className="w-4 h-4" /> Stems
                      </a>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* License terms */}
      <div className="mt-12 card-base p-6" data-testid="license-terms-block">
        <h2 className="font-display text-2xl font-bold text-[var(--text)]">License Terms</h2>
        <p className="mt-3 text-sm text-[var(--text-dim)] whitespace-pre-line leading-relaxed">
          {beat.license_terms || "Non-exclusive license. Buyer may use the beat for personal and commercial recordings up to 100,000 streams/sales. Producer retains ownership. Credit 'Prod. SonnyManeBeatz' in your song metadata."}
        </p>
      </div>
    </div>
  );
}
